import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { useRevealVisible } from "@/hooks/useRevealVisible";

export function Reveal({
  children,
  delay = 0,
  className,
  as: As = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: keyof typeof motion;
}) {
  const reduce = useReducedMotion();
  const { ref, visible } = useRevealVisible<HTMLDivElement>();
  const Comp = motion[As] as typeof motion.div;

  if (reduce) {
    return <Comp className={className}>{children}</Comp>;
  }

  // First render is always visible (SSR-safe); only below-the-fold content
  // animates in, so no section can ever stay blank if JS is slow or absent.
  return (
    <Comp
      ref={ref}
      className={className}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </Comp>
  );
}

export function KineticHeadline({ text, className }: { text: string; className?: string }) {
  const reduce = useReducedMotion();
  const { ref, visible } = useRevealVisible<HTMLSpanElement>();
  if (reduce) return <span className={className}>{text}</span>;
  const words = text.split(" ");
  return (
    <motion.span ref={ref} className={className}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          className="inline-block"
          animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: "0.4em" }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
            delay: visible ? 0.05 + i * 0.06 : 0,
          }}
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </motion.span>
  );
}
